import { motion } from 'framer-motion'
import { useState, useRef, useEffect, useCallback } from 'react'
import { AlertTriangle, Check, Upload, Plus, X } from 'lucide-react'
import StockShapePicker from '../../../shared/StockShapePicker'
import StockDimensionPreviewBlock from './StockDimensionPreviewBlock'
import { STOCK_SHAPES, type ShapeId } from '../../../../lib/stock-shapes'
import type { JobWizardConfig } from '../../../../types'
import type { PartPlacement } from '../../../../services/stockAlignment'

interface Props {
  field: string
  prompt: string
  inputType:
    | 'text' | 'number' | 'select' | 'confirm' | 'machine_picker'
    | 'file_upload' | 'work_origin'
    | 'stock_shape_picker' | 'stock_dimensions_preview'
  priority: 'required' | 'recommended'
  options?: Array<{ value: string; label: string }>
  answered: boolean
  onAnswer: (field: string, value: string | number) => void
  onFilesDropped?: (field: string, files: File[]) => void
  onCreateNew?: () => void
  /** Work offsets set from the 3D preview picker — syncs to offset fields */
  externalOffsets?: Array<{ register: string; x: number; y: number; z: number }>
  /** Current accumulated wizard config — used by stock-shape/dimension question types */
  config?: Partial<JobWizardConfig>
  /** Shape suggestions from /api/agent/suggest-stock-shape (Task 19 populates this) */
  shapeSuggestions?: Array<{ shapeId: ShapeId; rationale: string; fit: 'best' | 'good' | 'wasteful' }>
  /** Extracted part geometry — required for the irregular_drawing alignment flow. */
  partGeometry?: Record<string, unknown>
}

const ALL_REGISTERS = ['G54', 'G55', 'G56', 'G57', 'G58', 'G59'] as const

/** Local state holder for the stock-dimension preview block.
 *  Defined here (not exported) so the hook lives in a stable component, not in
 *  a top-level branch of AgentQuestionBlock (which would violate hooks rules).
 *
 *  For irregular_drawing shapes, this wrapper also captures the user-uploaded
 *  stock geometry and the confirmed part placement (from the alignment viewer)
 *  so they can be included in the submitted answer payload.
 */
function StockDimensionInputsWrapper({
  shapeId, initial, units, partGeometry, onSubmit,
}: {
  shapeId: ShapeId
  initial: Record<string, number>
  units: 'mm' | 'in'
  partGeometry?: Record<string, unknown>
  onSubmit(payload: {
    dims: Record<string, number>
    shapeId: ShapeId
    stockGeometry?: Record<string, unknown>
    partPlacement?: PartPlacement
  }): void
}) {
  const [dims, setDims] = useState<Record<string, number>>(initial)
  // For irregular_drawing: track stock geometry + placement confirmed via the
  // alignment viewer. These are forwarded into the submit payload so the server
  // can persist them on the job.
  const [stockGeometry, setStockGeometry] = useState<Record<string, unknown> | undefined>(undefined)
  const [partPlacement, setPartPlacement] = useState<PartPlacement | undefined>(undefined)
  return (
    <StockDimensionPreviewBlock
      shapeId={shapeId}
      values={dims}
      units={units}
      partGeometry={partGeometry}
      onChange={setDims}
      onPlacementConfirm={(placement, stock) => {
        setPartPlacement(placement)
        setStockGeometry(stock)
        // Derive bbox from stock geometry envelope so the submit payload always
        // reflects the just-confirmed stock dimensions (avoids reading stale
        // `dims` state set in the same React tick).
        const env = (stock as { part_envelope?: { min_x: number; max_x: number; min_y: number; max_y: number; min_z: number; max_z: number } }).part_envelope
        const fresh = env
          ? { x: env.max_x - env.min_x, y: env.max_y - env.min_y, z: env.max_z - env.min_z }
          : dims
        // Alignment confirm IS the submit for irregular_drawing — there is no
        // separate Confirm button in that flow.
        onSubmit({ dims: fresh, shapeId, stockGeometry: stock, partPlacement: placement })
      }}
      onSubmit={() => onSubmit({ dims, shapeId, stockGeometry, partPlacement })}
    />
  )
}

export default function AgentQuestionBlock({ field, prompt, inputType, priority, options, answered, onAnswer, onFilesDropped, onCreateNew, externalOffsets, config, shapeSuggestions, partGeometry }: Props) {
  const [value, setValue] = useState('')
  const [xVal, setXVal] = useState('')
  const [yVal, setYVal] = useState('')
  const [zVal, setZVal] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const [showIssueBox, setShowIssueBox] = useState(false)

  // Multi-offset state for work_origin
  const [offsets, setOffsets] = useState<Array<{ register: string; x: string; y: string; z: string }>>([
    { register: 'G54', x: '0', y: '0', z: '0' },
  ])
  const [activeRow, setActiveRow] = useState(0)

  const isMachinePicker = field === 'machineProfile' || inputType === 'machine_picker'
    || /\bmachine\s+profile\b/i.test(prompt) || /\bselect.*machine\b/i.test(prompt)
  const isStockShapePicker = inputType === 'stock_shape_picker' || field === 'stockShape'
  const isStockDimsPreview = inputType === 'stock_dimensions_preview'
  const isStockDims = !isStockShapePicker && (
    isStockDimsPreview
    || field === 'stockDims' || field === 'stockSize'
    || /\bstock\b.*\b(dimensions|size)\b/i.test(prompt)
    || /\braw\s+(stock|material)\b.*\b(x|mm|in)\b/i.test(prompt)
    || /\b\d+\s*[×x]\s*\d+\s*[×x]\s*\d+\b/.test(prompt)
  )
  const isWorkOrigin = field === 'workOrigin' || inputType === 'work_origin'

  // Auto-detect confirmation questions — but NOT if it's stock dims/shape or work origin
  const isConfirm = !isStockDims && !isStockShapePicker && !isWorkOrigin && (
    inputType === 'confirm'
    || /\bis (this|that) correct\b/i.test(prompt)
    || /\bconfirm\b/i.test(prompt)
    || /\bverify\b.*\bcorrect/i.test(prompt)
  )

  // Parse suggested values from prompt text (e.g. "104 × 64 × 27")
  useEffect(() => {
    if (isStockDims && !xVal && !yVal && !zVal) {
      const match = prompt.match(/(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)/)
      if (match) {
        setXVal(match[1])
        setYVal(match[2])
        setZVal(match[3])
      }
    }
  }, [isStockDims, prompt])

  // Sync external offsets from 3D preview picker into local offset state
  useEffect(() => {
    if (externalOffsets && externalOffsets.length > 0 && isWorkOrigin) {
      setOffsets(externalOffsets.map(o => ({
        register: o.register,
        x: String(o.x),
        y: String(o.y),
        z: String(o.z),
      })))
    }
  }, [externalOffsets, isWorkOrigin])

  useEffect(() => {
    if (!answered && inputRef.current) {
      inputRef.current.focus()
    }
  }, [answered])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isWorkOrigin) {
      const valid = offsets.every(o =>
        !isNaN(parseFloat(o.x)) && !isNaN(parseFloat(o.y)) && !isNaN(parseFloat(o.z)),
      )
      if (!valid) return
      const payload = offsets.map(o => ({
        register: o.register,
        x: parseFloat(o.x),
        y: parseFloat(o.y),
        z: parseFloat(o.z),
      }))
      onAnswer(field, JSON.stringify(payload))
      return
    }
    if (!value.trim()) return
    const parsed = inputType === 'number' ? parseFloat(value) : value
    if (inputType === 'number' && isNaN(parsed as number)) return
    onAnswer(field, parsed)
  }

  const handleSelectClick = (optionValue: string) => {
    onAnswer(field, optionValue)
  }

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const dropped = Array.from(e.dataTransfer.files)
    if (dropped.length > 0 && onFilesDropped) {
      onFilesDropped(field, dropped)
    }
  }, [field, onFilesDropped])

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? [])
    if (selected.length > 0 && onFilesDropped) {
      onFilesDropped(field, selected)
    }
    e.target.value = ''
  }, [field, onFilesDropped])

  if (answered) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        className="flex items-center gap-2 py-0.5"
      >
        <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        <span className="font-mono text-xs text-slate-500">{prompt.replace(/\*\*/g, '')}</span>
      </motion.div>
    )
  }

  const inputClass = "bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-1.5 font-mono text-sm text-slate-200 placeholder:text-slate-600 focus:border-cyan-400 focus:outline-none transition-colors"

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="my-2 rounded-lg border border-cyan-500/20 bg-white/[0.02] px-4 py-3"
    >
      <div className="flex items-start gap-2 mb-2">
        <AlertTriangle className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${
          priority === 'required' ? 'text-amber-400' : 'text-slate-500'
        }`} />
        <span className="font-mono text-sm text-slate-200">{prompt.replace(/\*\*/g, '')}</span>
      </div>

      {inputType === 'file_upload' ? (
        <label
          onDragOver={e => { e.preventDefault(); setIsDragging(true) }}
          onDragLeave={e => { e.preventDefault(); setIsDragging(false) }}
          onDrop={handleFileDrop}
          className={`
            flex flex-col items-center justify-center gap-2 ml-5
            w-full rounded-lg cursor-pointer h-24
            border-2 border-dashed transition-all duration-200
            ${isDragging
              ? 'border-cyan-400 bg-cyan-500/10 shadow-[0_0_20px_rgba(6,182,212,0.3)]'
              : 'border-cyan-500/30 bg-white/[0.02] hover:border-cyan-500/50 hover:bg-white/[0.04]'
            }
          `}
        >
          <input
            type="file"
            accept=".dwg,.dxf,.DWG,.DXF,.step,.stp,.STEP,.STP,.pdf,.PDF"
            multiple
            className="sr-only"
            onChange={handleFileInput}
          />
          <Upload className={`w-5 h-5 transition-colors ${isDragging ? 'text-cyan-400' : 'text-slate-500'}`} />
          <span className={`font-mono text-xs transition-colors ${isDragging ? 'text-cyan-300' : 'text-slate-400'}`}>
            Drop files here or click to browse
          </span>
        </label>
      ) : !isStockShapePicker && (inputType === 'select' || inputType === 'machine_picker' || isMachinePicker) && options ? (
        <div className="flex flex-wrap gap-2 ml-5">
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleSelectClick(opt.value)}
              className="px-3 py-1.5 rounded-lg font-mono text-xs border border-cyan-500/20 text-cyan-400 hover:border-cyan-400 hover:bg-cyan-500/10 transition-all duration-150"
            >
              {opt.label}
            </button>
          ))}
          {onCreateNew && (
            <button
              type="button"
              onClick={onCreateNew}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-xs border border-dashed border-slate-600 text-slate-400 hover:border-cyan-500/40 hover:text-cyan-400 hover:bg-cyan-500/5 transition-all duration-150"
            >
              <Plus className="w-3 h-3" />
              New Machine
            </button>
          )}
        </div>
      ) : isMachinePicker ? (
        // No saved machine profiles — show a create CTA instead of a dead-end
        // text box. A typed value can't match a profile id, so the text input
        // would produce an invalid machineId.
        <div className="ml-5 space-y-2">
          <p className="font-mono text-xs text-slate-500">
            No machine profiles yet — create one to continue.
          </p>
          {onCreateNew && (
            <button
              type="button"
              onClick={onCreateNew}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-xs border border-dashed border-slate-600 text-slate-400 hover:border-cyan-500/40 hover:text-cyan-400 hover:bg-cyan-500/5 transition-all duration-150"
            >
              <Plus className="w-3 h-3" />
              Create a machine profile
            </button>
          )}
        </div>
      ) : isConfirm ? (
        <div className="ml-5 space-y-2">
          {!showIssueBox ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onAnswer(field, 'yes')}
                className="px-4 py-1.5 rounded-lg font-mono text-xs font-semibold border border-emerald-500/30 text-emerald-400 hover:border-emerald-400 hover:bg-emerald-500/10 transition-all duration-150"
              >
                Looks Good
              </button>
              <button
                type="button"
                onClick={() => setShowIssueBox(true)}
                className="px-4 py-1.5 rounded-lg font-mono text-xs font-semibold border border-amber-500/30 text-amber-400 hover:border-amber-400 hover:bg-amber-500/10 transition-all duration-150"
              >
                Something's Wrong
              </button>
            </div>
          ) : (
            <form onSubmit={e => { e.preventDefault(); if (value.trim()) onAnswer(field, value.trim()) }} className="space-y-2">
              <textarea
                value={value}
                onChange={e => setValue(e.target.value)}
                placeholder="Describe what's wrong..."
                rows={3}
                autoFocus
                className={`w-full resize-none ${inputClass}`}
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={!value.trim()}
                  className="px-4 py-1.5 rounded-lg font-mono text-xs font-semibold border border-cyan-500/30 text-cyan-400 hover:border-cyan-400 hover:bg-cyan-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
                >
                  Submit
                </button>
                <button
                  type="button"
                  onClick={() => { setShowIssueBox(false); setValue('') }}
                  className="px-4 py-1.5 rounded-lg font-mono text-xs text-slate-500 hover:text-slate-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      ) : isWorkOrigin ? (
        <div className="ml-5 space-y-2.5">
          <p className="font-mono text-[10px] text-cyan-400/60 italic">
            Tip: Click the ORIGIN button on the 3D preview to set this visually
          </p>
          {/* Snap chips — apply to active row */}
          {options && options.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {options.map(opt => {
                const active = offsets[activeRow]
                const isSelected = active && opt.value === `${active.x},${active.y},${active.z}`
                return (
                  <button key={opt.value} type="button"
                    onClick={() => {
                      const [x, y, z] = opt.value.split(',')
                      setOffsets(prev => prev.map((o, i) =>
                        i === activeRow ? { ...o, x: x ?? '0', y: y ?? '0', z: z ?? '0' } : o,
                      ))
                    }}
                    className={`rounded-full text-[10px] px-2.5 py-1 border font-mono transition-colors
                      ${isSelected
                        ? 'border-amber-400/50 bg-amber-500/15 text-amber-300'
                        : 'border-cyan-500/20 text-cyan-300/70 hover:border-cyan-500/40 hover:text-cyan-300'
                      }`}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          )}
          {/* Offset rows */}
          <form onSubmit={handleSubmit} className="space-y-2">
            {offsets.map((offset, idx) => {
              const usedRegisters = offsets.map(o => o.register).filter((_, i) => i !== idx)
              const availableRegisters = ALL_REGISTERS.filter(r => !usedRegisters.includes(r))
              const isActive = idx === activeRow
              return (
                <div
                  key={idx}
                  onClick={() => setActiveRow(idx)}
                  className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer transition-colors
                    ${isActive ? 'bg-cyan-500/5 border border-cyan-500/30' : 'border border-transparent hover:bg-white/[0.02]'}`}
                >
                  <div className="relative shrink-0">
                    <select
                      value={offset.register}
                      onChange={e => setOffsets(prev => prev.map((o, i) =>
                        i === idx ? { ...o, register: e.target.value } : o,
                      ))}
                      className="bg-white/5 border border-cyan-500/20 rounded-lg px-2 py-1.5 font-mono text-xs text-cyan-300 appearance-none cursor-pointer pr-6 focus:border-cyan-400 focus:outline-none"
                    >
                      {availableRegisters.map(r => (
                        <option key={r} value={r} className="bg-[#0d1526] text-slate-200">{r}</option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-cyan-500/60 text-[8px]">&#x25BE;</div>
                  </div>
                  {(['x', 'y', 'z'] as const).map(axis => (
                    <div key={axis} className="flex items-center gap-1">
                      <span className="font-mono text-xs text-slate-500 uppercase">{axis}</span>
                      <input
                        type="number"
                        step="any"
                        value={offset[axis]}
                        onChange={e => setOffsets(prev => prev.map((o, i) =>
                          i === idx ? { ...o, [axis]: e.target.value } : o,
                        ))}
                        onFocus={() => setActiveRow(idx)}
                        placeholder="0"
                        className={`${inputClass} w-[72px] !px-2 !py-1 !text-xs`}
                      />
                    </div>
                  ))}
                  {offsets.length > 1 && (
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation()
                        setOffsets(prev => prev.filter((_, i) => i !== idx))
                        setActiveRow(r => Math.min(r, offsets.length - 2))
                      }}
                      className="shrink-0 p-1 rounded text-red-400/40 hover:text-red-400 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )
            })}
            <div className="flex items-center justify-between pt-1">
              {offsets.length < 6 ? (
                <button
                  type="button"
                  onClick={() => {
                    const used = new Set(offsets.map(o => o.register))
                    const next = ALL_REGISTERS.find(r => !used.has(r))
                    if (!next) return
                    setOffsets(prev => [...prev, { register: next, x: '0', y: '0', z: '0' }])
                    setActiveRow(offsets.length)
                  }}
                  className="flex items-center gap-1 font-mono text-[10px] text-cyan-400/70 hover:text-cyan-400 transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  Add Offset
                </button>
              ) : <div />}
              <button
                type="submit"
                className="px-3 py-1.5 rounded-lg font-mono text-xs font-semibold border border-cyan-500/30 text-cyan-400 hover:border-cyan-400 hover:bg-cyan-500/10 transition-all duration-150"
              >
                Confirm
              </button>
            </div>
          </form>
        </div>
      ) : isStockShapePicker ? (
        <div className="ml-5">
          <StockShapePicker
            value={((config?.stockShape as ShapeId) ?? 'rectangular') as ShapeId}
            onChange={(id) => onAnswer(field, id)}
            suggestedIds={shapeSuggestions?.map(s => s.shapeId) ?? []}
            bestFitId={shapeSuggestions?.find(s => s.fit === 'best')?.shapeId}
            rationales={Object.fromEntries(
              (shapeSuggestions ?? []).map(s => [s.shapeId, s.rationale]),
            ) as Partial<Record<ShapeId, string>>}
          />
        </div>
      ) : isStockDims ? (
        <div className="ml-5">
          <StockDimensionInputsWrapper
            shapeId={((config?.stockShape as ShapeId) ?? 'rectangular') as ShapeId}
            initial={config?.stockSize?.dimensions ?? {
              // Seed from prompt regex match (preserves legacy XYZ extraction)
              ...(xVal ? { x: parseFloat(xVal) } : {}),
              ...(yVal ? { y: parseFloat(yVal) } : {}),
              ...(zVal ? { z: parseFloat(zVal) } : {}),
            }}
            units="mm"
            partGeometry={partGeometry}
            onSubmit={({ dims, shapeId, stockGeometry, partPlacement }) => {
              const bbox = STOCK_SHAPES[shapeId].toBoundingBox(dims)
              // For irregular_drawing, include the captured stockGeometry + placement
              // so the server can persist them. For other shapes these are undefined
              // and omitted from the payload.
              const payload: Record<string, unknown> = {
                shapeId,
                dimensions: dims,
                ...bbox,
              }
              if (shapeId === 'irregular_drawing') {
                if (stockGeometry) payload.stockGeometry = stockGeometry
                if (partPlacement) payload.partPlacement = partPlacement
              }
              onAnswer(field, JSON.stringify(payload))
            }}
          />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex gap-2 ml-5">
          <input
            ref={inputRef}
            type={inputType === 'number' ? 'number' : 'text'}
            step={inputType === 'number' ? 'any' : undefined}
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder={inputType === 'number' ? '0.0' : 'Type here...'}
            className={`flex-1 ${inputClass}`}
          />
          {inputType === 'number' && (
            <span className="font-mono text-xs text-slate-500 self-center">mm</span>
          )}
          <button
            type="submit"
            className="px-3 py-1.5 rounded-lg font-mono text-xs font-semibold border border-cyan-500/30 text-cyan-400 hover:border-cyan-400 hover:bg-cyan-500/10 transition-all duration-150"
          >
            Submit
          </button>
        </form>
      )}
    </motion.div>
  )
}
